import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Mail, Lock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { AUTH_API } from '@/config/api';
import { getValidRedirectPath } from '@/config/authRedirect';
import { finishAuthRedirect } from '@/config/hosts';

const API = AUTH_API;

export default function LoginForm({ onSuccess, onSwitchToRegister, onForgotPassword, role = 'patient' }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const normalizedEmail = formData.email.toLowerCase().trim();
      const response = await axios.post(
        `${API}/login`,
        { email: normalizedEmail, password: formData.password },
        {
          timeout: 60000,
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const { user } = response.data;
      if (!user) {
        throw new Error('Invalid response from server');
      }

      await login(user);
      if (onSuccess) {
        onSuccess(user);
      } else {
        const redirectParam = searchParams.get('redirect');
        const defaultDest = role === 'doctor' ? '/oncoagent/app' : '/app';
        const redirectPath = getValidRedirectPath(redirectParam);
        finishAuthRedirect(navigate, redirectPath || defaultDest);
      }
    } catch (err) {
      let errorMessage = 'Login failed. Please check your credentials.';
      if (err.response) {
        const status = err.response.status;
        const data = err.response.data;
        if (status === 429) errorMessage = 'Too many login attempts. Please wait and try again.';
        else if (status === 401) errorMessage = data?.detail || 'Incorrect email or password.';
        else if (status === 503) errorMessage = 'Cannot reach the server. Please try again in a moment.';
        else if (typeof data?.detail === 'string') errorMessage = data.detail;
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (!err.response) {
        errorMessage = 'Cannot reach the server. Please try again in a moment.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full rounded-[18px] bg-[#eef3ea] px-6 py-7 sm:px-7 sm:py-8 text-charcoal">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-forest-ink mb-1" style={{ fontFamily: "'Bricolage Grotesque', 'Instrument Sans', system-ui, sans-serif" }}>
          Sign in
        </h2>
        <p className="text-sm text-[#3d4a42]">
          {role === 'doctor' ? 'Access your OncoAgent clinical account' : 'Access your ByOnco account'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="text-sm bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-charcoal">
              <Mail className="h-5 w-5" />
            </span>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 pl-10 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink focus:border-forest-ink transition"
              required
              autoComplete="email"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-charcoal">
              <Lock className="h-5 w-5" />
            </span>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 pl-10 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink focus:border-forest-ink transition"
              required
              autoComplete="current-password"
            />
          </div>
        </div>

        <button
          type="submit"
          className="w-full inline-flex items-center justify-center rounded-full px-4 py-3 text-sm font-semibold bg-forest-ink text-cream-paper hover:bg-forest-shadow disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </button>

        {onForgotPassword && (
          <p className="text-sm text-center">
            <button
              type="button"
              className="text-gray-600 hover:text-gray-900 underline underline-offset-2"
              onClick={onForgotPassword}
            >
              Forgot password?
            </button>
          </p>
        )}

        {onSwitchToRegister && (
          <p className="text-sm text-center text-gray-600">
            No account?{' '}
            <button
              type="button"
              className="text-gray-900 font-medium underline underline-offset-2"
              onClick={onSwitchToRegister}
            >
              Register
            </button>
          </p>
        )}
      </form>
    </div>
  );
}
